const STORAGE_KEY = 'van-guided-journey';

function readAll() {
	try {
		return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
	} catch (e) {
		return {};
	}
}

function writeAll(data) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	} catch (e) {
		// private browsing — progress won't persist
	}
}

function journeyKey(slug) {
	return slug;
}

export function loadJourneyProgress(slug) {
	const all = readAll();
	return all[journeyKey(slug)] || {
		stepStatus: {},
		taskChecks: {},
		taskChoices: {},
		documents: {},
		packing: {},
		activeStepId: null,
	};
}

export function saveJourneyProgress(slug, progress) {
	const all = readAll();
	all[journeyKey(slug)] = progress;
	writeAll(all);
}

export function setStepStatus(slug, stepId, status) {
	const p = loadJourneyProgress(slug);
	p.stepStatus[stepId] = status;
	saveJourneyProgress(slug, p);
	return p;
}

export function setActiveStep(slug, stepId) {
	const p = loadJourneyProgress(slug);
	p.activeStepId = stepId;
	saveJourneyProgress(slug, p);
	return p;
}

export function toggleTaskCheck(slug, scope, itemId) {
	const p = loadJourneyProgress(slug);
	if (!p.taskChecks[scope]) p.taskChecks[scope] = {};
	p.taskChecks[scope][itemId] = !p.taskChecks[scope][itemId];
	saveJourneyProgress(slug, p);
	return p;
}

export const STEP_CONFIRM_ID = 'step-confirmed';

export function isStepConfirmed(progress, scope) {
	return isTaskChecked(progress, scope, STEP_CONFIRM_ID);
}

export function isTaskChecked(progress, scope, itemId) {
	return !!(progress.taskChecks[scope] && progress.taskChecks[scope][itemId]);
}

export function confirmStep(slug, scope) {
	const p = loadJourneyProgress(slug);
	if (!p.taskChecks[scope]) p.taskChecks[scope] = {};
	p.taskChecks[scope][STEP_CONFIRM_ID] = true;
	saveJourneyProgress(slug, p);
	return p;
}

export function toggleDocument(slug, docId) {
	const p = loadJourneyProgress(slug);
	p.documents[docId] = !p.documents[docId];
	saveJourneyProgress(slug, p);
	return p;
}

export function togglePackingItem(slug, itemId) {
	const p = loadJourneyProgress(slug);
	p.packing[itemId] = !p.packing[itemId];
	saveJourneyProgress(slug, p);
	return p;
}

export function getStepProgress(step, progress, journey) {
	if (progress.stepStatus[step.id] === 'complete') {
		return { status: 'complete', label: 'Complete', fraction: 1 };
	}

	const guided = step.guided || {};
	const checks = guided.taskChecklist || [];
	const scope = step.id;

	if (guided.usesPackingChecklist && journey?.packingChecklist) {
		const total = journey.packingChecklist.length;
		const done = Object.values(progress.packing || {}).filter(Boolean).length;
		if (total > 0) {
			const fraction = done / total;
			return {
				status: fraction >= 1 ? 'complete' : fraction > 0 ? 'in_progress' : 'pending',
				label: fraction >= 1 ? 'Complete' : `${done}/${total} packed`,
				fraction,
			};
		}
	}

	if (checks.length > 0) {
		const confirmed = isStepConfirmed(progress, scope);
		return {
			status: confirmed ? 'complete' : 'pending',
			label: confirmed ? 'Complete' : 'Confirm to continue',
			fraction: confirmed ? 1 : 0,
		};
	}

	const manual = progress.stepStatus[step.id];
	if (manual === 'in_progress') return { status: 'in_progress', label: 'In progress', fraction: 0.5 };
	return { status: 'pending', label: 'Waiting…', fraction: 0 };
}

export function getOverallProgress(journey, progress) {
	const fractions = journey.steps.map((step) => getStepProgress(step, progress).fraction);
	const avg = fractions.reduce((a, b) => a + b, 0) / fractions.length;
	return Math.round(avg * 100);
}

export function getDocumentsProgress(journey, progress) {
	const total = journey.documentsWallet.length;
	const done = journey.documentsWallet.filter((d) => progress.documents[d.id]).length;
	return { done, total, fraction: total ? done / total : 0 };
}

export function downloadChecklistText(journey, progress) {
	const lines = [`${journey.title} — Packing Checklist`, `Generated: ${new Date().toLocaleDateString()}`, ''];
	journey.packingChecklist.forEach((item) => {
		const checked = progress.packing[item.id] ? '✓' : '□';
		lines.push(`${checked} ${item.label}`);
	});
	return lines.join('\n');
}

export function downloadDocumentsText(journey, progress) {
	const lines = [`${journey.title} — Documents Wallet`, `Generated: ${new Date().toLocaleDateString()}`, ''];
	journey.documentsWallet.forEach((doc) => {
		const checked = progress.documents[doc.id] ? '✓' : '□';
		lines.push(`${checked} ${doc.label} — ${doc.note}`);
	});
	return lines.join('\n');
}

export function triggerDownload(filename, text) {
	const blob = new Blob([text], { type: 'text/plain' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}
